/**
 * Landing (§15.2 #1): create a room, or join one by its code (also `/join/CODE` from the room's
 * QR code). Name + colour; every colour has a name and a symbol.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Swatch } from "../components/PlayerChip.tsx";
import { ApiError, createRoom, joinRoom, roomInfo } from "../lib/api.ts";
import { COLOURS } from "../lib/colours.ts";
import { navigate } from "../lib/router.ts";
import { lastRoom, loadSession, saveSession } from "../lib/session.ts";
import { readLocal, writeLocal } from "../lib/storage.ts";

const NAME_KEY = "handshake:name";
const COLOUR_KEY = "handshake:colour";

export function Landing({ code: initialCode }: { code: string | null }) {
  const [mode, setMode] = useState<"join" | "create">("join");
  const [code, setCode] = useState(initialCode ?? "");
  const [name, setName] = useState(() => readLocal(NAME_KEY) ?? "");
  const [colour, setColour] = useState(() => readLocal(COLOUR_KEY) ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [rejoin] = useState(() => lastRoom());

  const cleanCode = code.trim().toUpperCase();

  // A known session for this room: go straight back in.
  useEffect(() => {
    if (initialCode && loadSession(initialCode)) navigate(`/play/${initialCode}`, true);
  }, [initialCode]);

  useEffect(() => {
    if (mode !== "join" || cleanCode.length !== 4) {
      setInfo(null);
      return;
    }
    let cancelled = false;
    roomInfo(cleanCode).then(
      (r) => {
        if (cancelled) return;
        setInfo(
          r.joinable
            ? `Room ${r.code}: ${r.players} ${r.players === 1 ? "guest" : "guests"} waiting.`
            : "That game has already started.",
        );
      },
      () => !cancelled && setInfo("No room has that code."),
    );
    return () => {
      cancelled = true;
    };
  }, [mode, cleanCode]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError("Enter your name.");
    if (!colour) return setError("Pick a colour.");
    if (mode === "join" && cleanCode.length !== 4) return setError("Enter the 4-letter room code.");
    setBusy(true);
    try {
      writeLocal(NAME_KEY, name.trim());
      writeLocal(COLOUR_KEY, colour);
      const s =
        mode === "create"
          ? await createRoom(name.trim(), colour)
          : await joinRoom(cleanCode, name.trim(), colour);
      saveSession(s);
      navigate(`/play/${s.code}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app">
      <main className="main no-tabs stack">
        <div className="landing-title">
          <h1>HANDSHAKE</h1>
          <p className="muted">A gala of quiet exchanges.</p>
        </div>

        {rejoin && rejoin !== initialCode ? (
          <button type="button" className="btn block" onClick={() => navigate(`/play/${rejoin}`)}>
            Return to room {rejoin}
          </button>
        ) : null}

        <div className="toggle" role="tablist" aria-label="Join or create">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "join"}
            className={`btn ${mode === "join" ? "primary" : ""}`}
            onClick={() => setMode("join")}
          >
            Join a room
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "create"}
            className={`btn ${mode === "create" ? "primary" : ""}`}
            onClick={() => setMode("create")}
          >
            Host a new room
          </button>
        </div>

        <form className="panel stack" onSubmit={submit}>
          {mode === "join" ? (
            <label className="field">
              <span>Room code</span>
              <input
                className="text code"
                name="code"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                maxLength={4}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD"
                aria-describedby="room-info"
              />
              <span id="room-info" className="faint" style={{ marginTop: "0.35rem" }}>
                {info ?? "Scan the host's QR code, or type the code on their screen."}
              </span>
            </label>
          ) : (
            <p className="muted">
              You will be the host: you choose the setup and start the game, then play like everyone
              else.
            </p>
          )}

          <label className="field">
            <span>Your name</span>
            <input
              className="text"
              name="name"
              maxLength={24}
              autoComplete="nickname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
            />
          </label>

          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="muted" style={{ fontSize: "0.9rem", marginBottom: "0.35rem" }}>
              Your colour
            </legend>
            <div className="colour-grid">
              {COLOURS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={colour === c.id}
                  aria-label={c.label}
                  onClick={() => setColour(c.id)}
                  data-colour={c.id}
                >
                  <Swatch colour={c.id} />
                  {c.label}
                </button>
              ))}
            </div>
          </fieldset>

          {error ? (
            <p className="error-text" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="btn primary big block" disabled={busy}>
            {mode === "create" ? "Create room" : "Join"}
          </button>
        </form>
        <p className="faint center">Every phone looks the same. Never show your screen.</p>
        <p className="center">
          <a
            href="/intro"
            data-testid="landing-intro"
            onClick={(e) => {
              e.preventDefault();
              navigate("/intro");
            }}
          >
            New here? Watch how to play
          </a>
        </p>
        <p className="center">
          <a
            href="/screen"
            data-testid="use-as-screen"
            onClick={(e) => {
              e.preventDefault();
              navigate("/screen");
            }}
          >
            Use this device as a big screen
          </a>
        </p>
      </main>
    </div>
  );
}
